/**
 * Account enrichment bridge — wires the pure pipeline (enrichPipeline.ts)
 * and the thin EnrichPanel to the REAL seams:
 *
 *   • worker path: argoWorker.runAgent with the strict no-fabrication
 *     research prompt from enrichment.ts, parsed defensively — this
 *     revives the previously-dead enrichment module as the live worker
 *     leg. Unconfigured / failed / empty → null, so the panel falls back
 *     to the deterministic no-hallucination pipeline.
 *   • commit path: every approved or auto-applied field becomes a
 *     governed `update_brain` action through the EXISTING approvals seal
 *     (queueApproval → resolveApproval), so enrichment writes inherit the
 *     audit log, idempotency fence, and ledger like every agent write.
 *   • revert path: a compensating `update_brain` restoring the sealed
 *     before-value, through the same pipeline — provable revert, not a
 *     UI trick. Fields with no prior value refuse honestly.
 *
 * No component imports network code: EnrichPanel receives these as
 * injected props from the record surface (AccountDetailModal).
 */

import { isConfigured, runAgent } from '../agent/argoWorker';
import {
  accountDomain,
  buildEnrichmentPrompt,
  parseEnrichmentReply,
} from '../agent/enrichment';
import { queueApproval, resolveApproval } from '../agent/approvals';
import type { Account } from '../db/types';
import type { UpdateBrainAction } from '../agent/types';
import type { EnrichActionDiff, EnrichField } from './enrichPipeline';

/** The plain-data record shape the pure pipeline derives from. */
export function accountEnrichRecord(account: Account): Record<string, any> {
  return {
    id: account.id,
    recordType: 'account' as const,
    name: account.name,
    email: account.pocEmail,
    title: account.pocTitle,
  };
}

/**
 * Real worker leg: ask the hosted agent-worker (Claude) to research the
 * account with the strict-JSON, omit-what-you-cannot-support prompt.
 * Returns null whenever anything is unavailable or empty — the caller's
 * deterministic fallback then guarantees no hallucinated write.
 */
export async function fetchAccountWorkerFields(
  account: Account,
): Promise<EnrichField[] | null> {
  if (!isConfigured()) return null;
  try {
    const { text } = await runAgent(
      {
        id: `enrich-${account.id}`,
        name: 'Account Enrichment',
        template: 'enrichment',
        persona: buildEnrichmentPrompt(account),
      },
      {
        account: {
          id: account.id,
          name: account.name,
          domain: accountDomain(account),
          workflows: account.workflows.map((w) => w.name),
        },
      },
    );
    const parsed = parseEnrichmentReply(text);
    const fields: EnrichField[] = [];
    const push = (field: string, after: string | undefined) => {
      if (!after) return;
      fields.push({ field, before: null, after, source: 'worker', confidence: 0.6 });
    };
    push('industry', parsed.industry);
    push('size', parsed.size);
    push('summary', parsed.summary);
    push('stakeholderRoles', parsed.stakeholderRoles?.join(', '));
    return fields.length > 0 ? fields : null;
  } catch {
    return null; // graceful deterministic fallback — never fabricate
  }
}

/** Where each enrichment field lands in the account's Company Brain. */
const BRAIN_TARGETS: Record<
  string,
  { section: UpdateBrainAction['section']; field: string; label: string }
> = {
  domain: { section: 'identity', field: 'domain', label: 'Domain' },
  name: { section: 'identity', field: 'name', label: 'Company name' },
  title: { section: 'relationships', field: 'pocTitle', label: 'POC title' },
  seniority: { section: 'relationships', field: 'pocSeniority', label: 'POC seniority' },
  industry: { section: 'identity', field: 'industry', label: 'Industry' },
  size: { section: 'identity', field: 'size', label: 'Company size' },
  summary: { section: 'identity', field: 'summary', label: 'Company summary' },
  stakeholderRoles: {
    section: 'relationships',
    field: 'stakeholderRoles',
    label: 'Likely stakeholder roles',
  },
};

/** Map one enrichment field onto a governed `update_brain` action. */
export function enrichFieldToBrainAction(
  account: Account,
  field: EnrichField,
  evidence: string,
  value?: string,
): UpdateBrainAction {
  const target = BRAIN_TARGETS[field.field] ?? {
    section: 'identity' as const,
    field: field.field,
    label: field.field,
  };
  return {
    kind: 'update_brain',
    accountId: account.id,
    accountName: account.name,
    section: target.section,
    field: target.field,
    label: target.label,
    value: value ?? String(field.after),
    confidence: field.confidence,
    evidence,
  };
}

/**
 * Commit one field through the existing approvals seal: queue the governed
 * action with the diff's provenance note, then resolve it approved (the
 * operator's per-field Approve — or the earned auto tier — IS the review).
 * resolveApproval owns execution, the idempotency fence, and audit logging.
 */
export async function commitEnrichField(
  account: Account,
  field: EnrichField,
  diff: EnrichActionDiff,
): Promise<void> {
  const action = enrichFieldToBrainAction(account, field, diff.provenanceNote);
  const approval = await queueApproval(action, 'chat', diff.provenanceNote);
  await resolveApproval(approval.id, 'approved');
}

/** Commit an auto-applied batch, field by field, through the same seal. */
export async function commitEnrichFields(
  account: Account,
  fields: EnrichField[],
  diff: EnrichActionDiff,
): Promise<void> {
  for (const field of fields) {
    await commitEnrichField(account, field, diff);
  }
}

/**
 * Provable revert: re-propose the sealed before-value through the same
 * governed pipeline. Refuses honestly when no prior value exists —
 * restoring "nothing" would fabricate an empty fact instead of undoing.
 */
export async function revertEnrichField(
  account: Account,
  field: EnrichField,
  diff: EnrichActionDiff,
): Promise<void> {
  const before = field.before;
  if (before == null || String(before).trim() === '') {
    throw new Error(
      `No prior value was sealed for “${field.field}” — remove the fact from the ` +
        'Company Brain (or revert the entry in the Decision Ledger) instead.',
    );
  }
  const note = `⟲ reverts enrichment — ${diff.provenanceNote}`;
  const action = enrichFieldToBrainAction(account, field, note, String(before));
  const approval = await queueApproval(action, 'chat', note);
  await resolveApproval(approval.id, 'approved');
}
